import { useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import type { Charges } from '../types'
import { calculerChargesFixesMensuelles, chargesParCategorie } from '../calculs'

interface Props { charges: Charges; onChange: (c: Charges) => void }
type F = { key: keyof Charges; label: string }
type Cat = { id: string; icon: string; label: string; catKey: string; fields: F[] }

const CATS: Cat[] = [
  { id:'structure', icon:'🏠', label:'Loyer & Structure', catKey:'Structure',
    fields:[{key:'loyer',label:'Loyer'},{key:'chargesLocatives',label:'Charges locatives'},{key:'credit',label:'Crédit professionnel'},{key:'leasingMateriel',label:'Leasing matériel'},{key:'cfe',label:'CFE'},{key:'taxeFonciere',label:'Taxe foncière'},{key:'electricite',label:'Électricité'},{key:'eau',label:'Eau'},{key:'assurance',label:'Assurance multirisque'},{key:'entretien',label:'Entretien / nettoyage'},{key:'alarme',label:'Alarme / surveillance'}]},
  { id:'vehicule', icon:'🚗', label:'Déplacements & Véhicules', catKey:'Déplacements',
    fields:[{key:'fraisKilometriques',label:'Frais kilométriques'},{key:'achatVehicule',label:'Achat véhicule (amorti)'},{key:'leasingVehicule',label:'Leasing véhicule'},{key:'essence',label:'Essence / carburant'},{key:'assuranceVehicule',label:'Assurance véhicule'},{key:'transportAchat',label:"Transport d'achat"},{key:'autresVehicule',label:'Autres véhicule'}]},
  { id:'metier', icon:'✂️', label:'Frais Métier', catKey:'Métier',
    fields:[{key:'petitMateriel',label:'Petit matériel'},{key:'grosMateriel',label:'Gros matériel'},{key:'produitsTechniquesCharges',label:'Produits techniques complémentaires'},{key:'vetementsProf',label:'Vêtements professionnels'},{key:'logicielCoiffure',label:'Logiciel coiffure'},{key:'decoration',label:'Décoration / PLV'},{key:'autresMetier',label:'Autres frais métier'}]},
  { id:'gestion', icon:'🗂️', label:'Gestion & Administration', catKey:'Admin',
    fields:[{key:'expertComptable',label:'Expert-comptable'},{key:'fraisJuridiques',label:'Frais juridiques'},{key:'honorairesSocial',label:'Honoraires social'},{key:'fraisBancaires',label:'Frais bancaires'},{key:'fraisTPE',label:'Frais TPE / CB'},{key:'logicielCaisse',label:'Logiciel de caisse'},{key:'telephoneFixe',label:'Téléphone fixe'},{key:'telephoneMobile',label:'Téléphone mobile'},{key:'hebergementWeb',label:'Site web'},{key:'fournitures',label:'Fournitures'},{key:'presse',label:'Presse / abonnements'},{key:'cadeauxClients',label:'Cadeaux clients'},{key:'cotisationsGroupement',label:'Cotisations groupement'}]},
  { id:'social', icon:'👥', label:'Charges Sociales Salariés', catKey:'Social sal.',
    fields:[{key:'medecine',label:'Médecine du travail'},{key:'mutuelleSalaries',label:'Mutuelle salariés'},{key:'epargneSalariale',label:'Épargne salariale'},{key:'adsams',label:'ADSAMS'},{key:'taxeApprentissage',label:"Taxe d'apprentissage"},{key:'opco',label:'OPCO'}]},
  { id:'marketing', icon:'📣', label:'Marketing & Formation', catKey:'Marketing',
    fields:[{key:'coaching',label:'Coaching / consultant'},{key:'marketingDigital',label:'Marketing digital'},{key:'communication',label:'Communication'},{key:'formationCoiffure',label:'Formation coiffure'},{key:'formationManagement',label:'Formation management'}]},
  { id:'droits', icon:'🎵', label:'Droits & Licences', catKey:'Droits',
    fields:[{key:'sacem',label:'SACEM'},{key:'spre',label:'SPRE'}]},
  { id:'autres', icon:'📦', label:'Autres Charges', catKey:'Autres',
    fields:[{key:'soustraitance',label:'Sous-traitance'},{key:'fraisReception',label:'Frais de réception'},{key:'autresCharges',label:'Autres charges'}]},
]

const e0 = (n: number) =>
  new Intl.NumberFormat('fr-FR',{style:'currency',currency:'EUR',maximumFractionDigits:0}).format(n)

// Composant double-saisie mensuel ⇔ annuel
function DualInput({ fieldKey, label, valueMois, onChange }: {
  fieldKey: string; label: string; valueMois: number; onChange: (mois: number) => void
}) {
  // On stocke toujours en mensuel ; l'annuel = mensuel × 12
  const valueAn = valueMois * 12

  const handleMois = (v: string) => {
    const n = parseFloat(v) || 0
    onChange(n)
  }
  const handleAn = (v: string) => {
    const n = parseFloat(v) || 0
    onChange(n / 12)
  }

  return (
    <div>
      <label className="block text-xs text-gray-500 mb-1">{label}</label>
      <div className="flex gap-1.5">
        {/* Mensuel */}
        <div className="relative flex-1">
          <input
            type="number" min={0} step={10}
            value={valueMois || ''}
            onChange={e => handleMois(e.target.value)}
            placeholder="0"
            className="w-full pl-2 pr-14 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
          <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-xs text-blue-600 font-medium select-none">
            /mois
          </span>
        </div>
        {/* Annuel */}
        <div className="relative flex-1">
          <input
            type="number" min={0} step={100}
            value={valueAn ? Math.round(valueAn) : ''}
            onChange={e => handleAn(e.target.value)}
            placeholder="0"
            className="w-full pl-2 pr-10 py-1.5 text-sm border border-dashed border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-200 text-gray-600"
          />
          <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-xs text-green-600 font-medium select-none">
            /an
          </span>
        </div>
      </div>
    </div>
  )
}

export default function SectionCharges({ charges, onChange }: Props) {
  const [open, setOpen] = useState<Record<string,boolean>>({ structure: true })
  const tog = (id: string) => setOpen(p => ({ ...p, [id]: !p[id] }))
  const set = (key: keyof Charges, mois: number) => onChange({ ...charges, [key]: mois })

  const totalMois = calculerChargesFixesMensuelles(charges)
  const byCat = chargesParCategorie(charges)

  return (
    <section className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4" style={{ background: '#FFFBEB' }}>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-base">💰</span>
              <h3 className="font-semibold text-sm" style={{ color: '#92400E' }}>Charges fixes</h3>
            </div>
            <p className="text-xs mt-0.5" style={{ color: '#A16207' }}>
              Saisie <strong>HT</strong> — entrez le montant mensuel <em>ou</em> annuel, l'autre se calcule automatiquement
            </p>
          </div>
          <div className="text-right ml-4 flex-shrink-0">
            <div className="font-bold text-sm" style={{ color: '#92400E' }}>
              {e0(totalMois)} <span className="font-normal text-xs">HT/mois</span>
            </div>
            <div className="text-xs" style={{ color: '#A16207' }}>
              {e0(totalMois * 12)} HT/an
            </div>
          </div>
        </div>

        {/* Légende double saisie */}
        <div className="flex gap-3 mt-2 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded border border-blue-400 bg-white"/>
            <span className="text-blue-600 font-medium">Mensuel HT</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded border border-dashed border-green-500 bg-gray-50"/>
            <span className="text-green-600 font-medium">Annuel HT (auto)</span>
          </div>
          <span className="text-gray-400">— modifiez l'un ou l'autre</span>
        </div>
      </div>

      <div className="bg-white divide-y divide-gray-50">
        {CATS.map(cat => {
          const catTotal = byCat[cat.catKey] ?? 0
          const isOpen = !!open[cat.id]

          return (
            <div key={cat.id}>
              <button onClick={() => tog(cat.id)}
                className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-2">
                  <span className="text-sm">{cat.icon}</span>
                  <span className="text-sm font-medium text-gray-700">{cat.label}</span>
                  {catTotal === 0 && <span className="text-xs text-gray-300">vide</span>}
                </div>
                <div className="flex items-center gap-3">
                  {catTotal > 0 && (
                    <div className="text-right">
                      <span className="text-xs font-semibold" style={{ color: '#E65100' }}>
                        {e0(catTotal)}<span className="font-normal text-gray-400"> /mois</span>
                      </span>
                      <span className="text-xs text-gray-400 ml-2">
                        {e0(catTotal * 12)}<span className="font-normal"> /an</span>
                      </span>
                    </div>
                  )}
                  {catTotal === 0 && <span className="text-xs text-gray-300">0 €</span>}
                  {isOpen ? <ChevronUp className="w-3.5 h-3.5 text-gray-400"/> : <ChevronDown className="w-3.5 h-3.5 text-gray-400"/>}
                </div>
              </button>

              {isOpen && (
                <div className="px-5 pb-4 pt-2 bg-gray-50/40">
                  {/* En-têtes colonnes */}
                  <div className="grid grid-cols-2 gap-1.5 mb-1 pr-1">
                    <div/>
                    <div className="grid grid-cols-2 gap-1.5">
                      <span className="text-xs font-medium text-blue-500 text-center">€ HT / mois</span>
                      <span className="text-xs font-medium text-green-500 text-center">€ HT / an</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {cat.fields.map(f => (
                      <DualInput
                        key={f.key as string}
                        fieldKey={f.key as string}
                        label={f.label}
                        valueMois={charges[f.key] as number}
                        onChange={mois => set(f.key, mois)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Récap total */}
      <div className="px-5 py-3 flex justify-between items-center"
        style={{ background: '#FFF9E6', borderTop: '1px solid #FDE68A' }}>
        <span className="text-sm font-semibold" style={{ color: '#92400E' }}>Total charges fixes</span>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <span className="font-bold" style={{ color: '#92400E' }}>{e0(totalMois)}</span>
            <span className="text-xs text-gray-500 ml-1">HT/mois</span>
          </div>
          <div className="text-right">
            <span className="font-bold" style={{ color: '#92400E' }}>{e0(totalMois * 12)}</span>
            <span className="text-xs text-gray-500 ml-1">HT/an</span>
          </div>
        </div>
      </div>
    </section>
  )
}
